import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Copy, RefreshCw, Wand2, Check } from "lucide-react";
import { generatePassword } from "@/lib/passwords";
import PasswordStrength from "@/components/PasswordStrength";
import { toast } from "sonner";

export default function PasswordGenerator({ onUse, triggerLabel = "Generate" }) {
  const [open, setOpen] = useState(false);
  const [length, setLength] = useState(20);
  const [useLower, setUseLower] = useState(true);
  const [useUpper, setUseUpper] = useState(true);
  const [useDigits, setUseDigits] = useState(true);
  const [useSymbols, setUseSymbols] = useState(true);
  const [pw, setPw] = useState(() => generatePassword({ length: 20 }));
  const [copied, setCopied] = useState(false);

  const regen = () => {
    const next = generatePassword({ length, useLower, useUpper, useDigits, useSymbols });
    setPw(next);
    setCopied(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pw);
      setCopied(true);
      toast.success("Password copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Unable to copy");
    }
  };

  const useIt = () => {
    if (onUse) onUse(pw);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) regen(); }}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="rounded-xl border-[color:var(--vault-border)] text-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-light)]"
          data-testid="open-password-generator-btn"
        >
          <Wand2 size={16} className="mr-1.5" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-xl">Password generator</DialogTitle>
        </DialogHeader>

        <div className="rounded-xl bg-[#F1F5FA] border border-[color:var(--vault-border)] p-3 mt-2">
          <div className="font-mono text-lg break-all text-[color:var(--vault-text-primary)]" data-testid="generated-password-value">
            {pw}
          </div>
          <div className="mt-3">
            <PasswordStrength value={pw} />
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          <Button onClick={regen} variant="outline" className="flex-1 rounded-xl" data-testid="regen-password-btn">
            <RefreshCw size={16} className="mr-1.5" /> Regenerate
          </Button>
          <Button onClick={copy} variant="outline" className="flex-1 rounded-xl" data-testid="copy-generated-password-btn">
            {copied ? <Check size={16} className="mr-1.5" /> : <Copy size={16} className="mr-1.5" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Length</Label>
              <span className="font-mono text-sm" data-testid="generator-length-value">{length}</span>
            </div>
            <Slider
              min={8}
              max={48}
              step={1}
              value={[length]}
              onValueChange={(v) => { setLength(v[0]); }}
              className="mt-2"
              data-testid="generator-length-slider"
            />
          </div>

          {[
            ["Lowercase (a–z)", useLower, setUseLower, "lower"],
            ["Uppercase (A–Z)", useUpper, setUseUpper, "upper"],
            ["Digits (0–9)", useDigits, setUseDigits, "digits"],
            ["Symbols (!@#…)", useSymbols, setUseSymbols, "symbols"],
          ].map(([label, value, set, key]) => (
            <div key={key} className="flex items-center justify-between">
              <Label className="text-sm">{label}</Label>
              <Switch
                checked={value}
                onCheckedChange={set}
                data-testid={`toggle-${key}`}
              />
            </div>
          ))}
        </div>

        <DialogFooter className="mt-4">
          {onUse && (
            <Button onClick={useIt} className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" data-testid="use-generated-password-btn">
              Use this password
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
